import React from 'react';

import { Box, FormControl, FormHelperText, InputLabel, MenuItem, Select, Typography } from '@mui/material';

import { useGetCustomerServiceReps } from '~/hooks/apis/loyalty/useCustomerServiceReps';

interface Props {
  /** Currently selected rep id, or null for "none selected". */
  value: string | null;
  onChange: (id: string | null) => void;
  label?: string;
  /** Mark as required and surface a helper message when value is null. */
  required?: boolean;
  /** Show error styling (e.g. on form submit attempt without a selection). */
  error?: boolean;
  helperText?: React.ReactNode;
  disabled?: boolean;
}

/**
 * Reusable select that lists customer service reps for assignment flows
 * (enrol, change tier, change rep). The list is fetched paginated; the first
 * page (default 100) is loaded for the dropdown.
 */
export default function CustomerServiceRepSelector({
  value,
  onChange,
  label = 'Customer Service Representative',
  required,
  error,
  helperText,
  disabled,
}: Props) {
  const { data, isLoading, isError, error: queryError } = useGetCustomerServiceReps({ page: 1, pageSize: 50 });
  const reps = data?.result.reps ?? [];

  const hasOptions = reps.length > 0;
  const computedHelperText = (() => {
    if (helperText) {
      return helperText;
    }
    if (isError) {
      return queryError instanceof Error ? `Failed to load reps: ${queryError.message}` : 'Failed to load reps.';
    }
    if (!isLoading && !hasOptions) {
      return 'No reps available — add one in Infinite Concierge.';
    }
    if (required && !value) {
      return 'Select a representative.';
    }
    return null;
  })();

  return (
    <FormControl fullWidth required={required} error={error} disabled={disabled || isLoading}>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={value ?? ''}
        onChange={(e) => {
          const next = e.target.value;
          onChange(next ? String(next) : null);
        }}
      >
        {reps.map((rep) => (
          <MenuItem key={rep.id} value={rep.id}>
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {rep.firstName} {rep.lastName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {rep.email}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
      {computedHelperText && <FormHelperText>{computedHelperText}</FormHelperText>}
    </FormControl>
  );
}
